// AI Sales Chat Assistant — per-lead panel + global floating chat.
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { studioHeaders } from "@/lib/studioAuth";
import { useLang, tt } from "@/pages/studio/sbLang";
import { MessageCircle, Send, Loader2, Plus, X, History, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function SalesChatPanel({ leadId, height = 420 }) {
    const lang = useLang();
    const [sessionId, setSessionId] = useState(null);
    const [msgs, setMsgs] = useState([]);
    const [input, setInput] = useState("");
    const [busy, setBusy] = useState(false);
    const [sessions, setSessions] = useState([]);
    const [showHistory, setShowHistory] = useState(false);
    const bottomRef = useRef(null);

    const loadSessions = async () => {
        try {
            const { data } = await axios.get(`${API}/sales-brain/chat/sessions`, {
                headers: studioHeaders(), params: leadId ? { lead_id: leadId } : {},
            });
            setSessions(data || []);
        } catch { /* silent */ }
    };
    useEffect(() => { setSessionId(null); setMsgs([]); loadSessions(); }, [leadId]); // eslint-disable-line
    useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, busy]);

    const openSession = async (sid) => {
        try {
            const { data } = await axios.get(`${API}/sales-brain/chat/sessions/${sid}`, { headers: studioHeaders() });
            setSessionId(sid); setMsgs(data.messages || []); setShowHistory(false);
        } catch { toast.error("Load failed"); }
    };
    const delSession = async (e, sid) => {
        e.stopPropagation();
        await axios.delete(`${API}/sales-brain/chat/sessions/${sid}`, { headers: studioHeaders() });
        if (sid === sessionId) { setSessionId(null); setMsgs([]); }
        loadSessions();
    };
    const send = async () => {
        const message = input.trim();
        if (!message || busy) return;
        setInput("");
        setMsgs((m) => [...m, { role: "user", content: message }]);
        setBusy(true);
        try {
            const { data } = await axios.post(`${API}/sales-brain/chat`,
                { session_id: sessionId, lead_id: leadId || null, message },
                { headers: studioHeaders(), timeout: 120000 });
            setSessionId(data.session_id);
            setMsgs((m) => [...m, { role: "assistant", content: data.reply }]);
            loadSessions();
        } catch (err) {
            toast.error(err?.response?.data?.detail || tt(lang, "AI failed — try again", "AI 回复失败 — 请重试"));
            setMsgs((m) => m.slice(0, -1));
            setInput(message);
        }
        setBusy(false);
    };

    const SUGGESTIONS = leadId
        ? [["How should I follow up today?", "今天该怎么跟进这个客户？"],
        ["Write the WhatsApp message for me", "帮我写一条 WhatsApp 跟进话术"],
        ["Client says it's too expensive — how to reply?", "客户说太贵了，怎么回？"]]
        : [["Which lead should I focus on today?", "今天我该优先跟进哪个客户？"],
        ["How do I pitch the monthly system?", "怎么推销 AI 月度内容系统？"]];

    return (
        <div className="flex flex-col" style={{ height }} data-testid="sb-chat-panel">
            <div className="flex items-center gap-2 pb-2 border-b border-cafe-espresso/10">
                <button onClick={() => { setShowHistory((v) => !v); loadSessions(); }} data-testid="sb-chat-history-btn"
                    className="flex items-center gap-1 text-[11px] text-cafe-mocha hover:text-cafe-espresso rounded-full border border-cafe-espresso/15 px-2.5 py-1">
                    <History className="h-3 w-3" /> {tt(lang, "History", "历史对话")} ({sessions.length})
                </button>
                <button onClick={() => { setSessionId(null); setMsgs([]); setShowHistory(false); }} data-testid="sb-chat-new-btn"
                    className="flex items-center gap-1 text-[11px] text-cafe-olive hover:text-cafe-oliveDark rounded-full border border-cafe-olive/30 px-2.5 py-1">
                    <Plus className="h-3 w-3" /> {tt(lang, "New chat", "新对话")}
                </button>
            </div>
            {showHistory && (
                <div className="border-b border-cafe-espresso/10 max-h-36 overflow-y-auto" data-testid="sb-chat-history-list">
                    {sessions.length === 0 && <p className="text-[11.5px] text-cafe-mocha py-3 text-center">{tt(lang, "No chats yet", "还没有历史对话")}</p>}
                    {sessions.map((s) => (
                        <div key={s.id} onClick={() => openSession(s.id)}
                            className="flex items-center gap-2 px-2 py-2 text-[12px] hover:bg-cafe-latte/50 cursor-pointer">
                            <span className="truncate flex-1 text-cafe-espresso">{s.title}</span>
                            {!leadId && s.business_name && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-cafe-latte text-cafe-mocha shrink-0">{s.business_name}</span>}
                            <Trash2 onClick={(e) => delSession(e, s.id)} className="h-3.5 w-3.5 text-cafe-mocha/50 hover:text-red-700 shrink-0" />
                        </div>))}
                </div>)}
            <div className="flex-1 overflow-y-auto py-3 space-y-3" data-testid="sb-chat-messages">
                {msgs.length === 0 && (
                    <div className="pt-4">
                        <p className="text-[12.5px] text-cafe-mocha text-center mb-4">
                            {tt(lang, "Ask your AI sales advisor anything — it knows this client's full analysis.",
                                "问 AI 销售顾问任何问题 — 它掌握该客户的完整分析数据。")}
                        </p>
                        <div className="flex flex-wrap gap-2 justify-center">
                            {SUGGESTIONS.map(([en, cn], i) => (
                                <button key={i} onClick={() => setInput(tt(lang, en, cn))} data-testid={`sb-chat-suggestion-${i}`}
                                    className="text-[11.5px] px-3 py-1.5 rounded-full border border-cafe-olive/30 text-cafe-olive hover:bg-cafe-olive/10">
                                    {tt(lang, en, cn)}
                                </button>))}
                        </div>
                    </div>)}
                {msgs.map((m, i) => (
                    <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                        <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-[13px] leading-relaxed whitespace-pre-wrap ${m.role === "user"
                            ? "bg-cafe-olive text-cafe-cream rounded-br-md"
                            : "bg-cafe-latte/70 text-cafe-espresso rounded-bl-md"}`}>
                            {m.content}
                        </div>
                    </div>))}
                {busy && (
                    <div className="flex justify-start">
                        <div className="rounded-2xl rounded-bl-md bg-cafe-latte/70 px-3.5 py-2.5 text-[13px] text-cafe-mocha flex items-center gap-2">
                            <Loader2 className="h-3.5 w-3.5 animate-spin" /> {tt(lang, "Thinking…", "思考中…")}
                        </div>
                    </div>)}
                <div ref={bottomRef} />
            </div>
            <div className="flex items-end gap-2 pt-2 border-t border-cafe-espresso/10">
                <textarea value={input} onChange={(e) => setInput(e.target.value)} rows={1} data-testid="sb-chat-input"
                    onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                    placeholder={tt(lang, "Ask about this client… (Enter to send)", "问 AI 顾问…（Enter 发送）")}
                    className="flex-1 rounded-xl border border-cafe-espresso/15 bg-white px-3.5 py-2.5 text-[13px] outline-none focus:border-cafe-olive resize-none" />
                <button onClick={send} disabled={busy || !input.trim()} data-testid="sb-chat-send"
                    className="rounded-full bg-cafe-olive hover:bg-cafe-oliveDark disabled:opacity-40 text-cafe-cream h-10 w-10 flex items-center justify-center shrink-0">
                    <Send className="h-4 w-4" />
                </button>
            </div>
        </div>
    );
}

export function GlobalSalesChat() {
    const lang = useLang();
    const [open, setOpen] = useState(false);
    const [leads, setLeads] = useState([]);
    const [leadId, setLeadId] = useState("");
    useEffect(() => {
        if (!open || leads.length) return;
        axios.get(`${API}/sales-brain/leads`, { headers: studioHeaders() })
            .then(({ data }) => setLeads((data || []).filter((r) => r.status === "ready")))
            .catch(() => { });
    }, [open]); // eslint-disable-line
    return (
        <>
            {!open && (
                <button onClick={() => setOpen(true)} data-testid="sb-chat-float-btn"
                    className="fixed bottom-6 right-6 z-40 rounded-full bg-cafe-espresso text-cafe-cream h-14 w-14 flex items-center justify-center shadow-xl hover:scale-105 transition-transform">
                    <MessageCircle className="h-6 w-6" strokeWidth={1.75} />
                </button>)}
            {open && (
                <div className="fixed bottom-6 right-6 z-40 w-[min(400px,calc(100vw-32px))] rounded-2xl border border-cafe-espresso/15 bg-cafe-cream shadow-2xl flex flex-col overflow-hidden" data-testid="sb-chat-float-window">
                    <div className="flex items-center gap-2 px-4 py-3 bg-cafe-espresso text-cafe-cream">
                        <MessageCircle className="h-4 w-4" />
                        <span className="text-[13px] font-medium">{tt(lang, "AI Sales Advisor", "AI 销售顾问")}</span>
                        <select value={leadId} onChange={(e) => setLeadId(e.target.value)} data-testid="sb-chat-lead-select"
                            className="ml-auto rounded-full bg-white/15 text-cafe-cream text-[11px] px-2.5 py-1 outline-none max-w-[150px]">
                            <option value="" className="text-cafe-espresso">{tt(lang, "General", "通用 · 不限客户")}</option>
                            {leads.map((l) => <option key={l.id} value={l.id} className="text-cafe-espresso">{l.business_name}</option>)}
                        </select>
                        <button onClick={() => setOpen(false)} data-testid="sb-chat-float-close" className="text-cafe-cream/70 hover:text-cafe-cream">
                            <X className="h-4 w-4" />
                        </button>
                    </div>
                    <div className="px-3.5 pb-3.5 pt-1">
                        <SalesChatPanel key={leadId} leadId={leadId || null} height={440} />
                    </div>
                </div>)}
        </>
    );
}
